import {
  Button,
  CheckInput,
  Modal,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import {
  FinancialBlockProcessItem,
  replaceFinancialBlockProcesses,
} from '../api';

import styles from './ProcessesModal.module.scss';

const dictPrefix = 'financialPortal.settings.financialBlock';

interface Props {
  isOpen: boolean;
  processes: FinancialBlockProcessItem[];
  onClose: () => void;
  onSaved: () => void;
}

export function ProcessesModal({
  isOpen,
  processes,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // El buscador va por el formulario y se lee con `useWatch`, NO con un
  // `onChange` propio: `TextInput` de global-ui es un input controlado por
  // `control` y no hay garantía de que reenvíe un `onChange` externo.
  const { control, reset } = useForm<{ search: string }>({
    defaultValues: { search: '' },
  });
  const search = useWatch({ control, name: 'search' }) ?? '';

  // Igual que en ConfigModal: el modal no se desmonta, así que hay que
  // reponer el estado en cada apertura o el usuario ve sus marcas anteriores.
  useEffect(() => {
    setSelected(processes.filter((p) => p.affected).map((p) => p.code));
    reset({ search: '' });
  }, [isOpen, processes, reset]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return processes;
    }
    return processes.filter((process) =>
      t(`${dictPrefix}.processNames.${process.code}`)
        .toLowerCase()
        .includes(needle),
    );
  }, [processes, search, t]);

  const toggle = useCallback((code: string) => {
    setSelected((prev) =>
      prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code],
    );
  }, []);

  const onSave = useCallback(async () => {
    setIsSaving(true);
    // Se manda el conjunto completo en el orden del catálogo, no en el orden
    // en que el usuario fue marcando: el backend reemplaza todo y un orden
    // estable hace los tests y los diffs legibles.
    const codes = processes
      .map((process) => process.code)
      .filter((code) => selected.includes(code));

    const { error } = await replaceFinancialBlockProcesses(codes);
    setIsSaving(false);

    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`${dictPrefix}.saveError`),
      });
      return;
    }
    addToast({
      icon: 'success',
      color: 'success',
      text: t(`${dictPrefix}.saveSuccess`),
    });
    onSaved();
  }, [onSaved, processes, selected, t]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <h1 className="text-dark fs-22 mb-2 text-center">
        {t(`${dictPrefix}.processesForm.title`)}
      </h1>
      <p className="fs-16 text-center mb-4">
        {t(`${dictPrefix}.processesForm.description`)}
      </p>
      <TextInput
        name="search"
        label={t(`${dictPrefix}.processesForm.searchLabel`)}
        placeholder={t(`${dictPrefix}.processesForm.searchPlaceholder`)}
        control={control}
      />
      <div
        className="border rounded"
        style={{ maxHeight: 260, overflowY: 'auto' }}
      >
        {visible.map((process) => (
          <div
            key={process.code}
            className={`${styles.processRow} d-flex align-items-center px-3 py-2 border-bottom`}
          >
            {/* `name` es también el `id` del input que renderiza CheckInput, así
                que tiene que ser único por fila. */}
            <CheckInput
              name={`process-${process.code}`}
              label={t(`${dictPrefix}.processNames.${process.code}`)}
              value={selected.includes(process.code)}
              onChange={() => toggle(process.code)}
            />
          </div>
        ))}
      </div>
      <Row className="mt-4">
        <Col xs={12} md={6}>
          <Button
            outlined
            fullwidth
            text={t(`${dictPrefix}.processesForm.cancel`)}
            onClick={onClose}
          />
        </Col>
        <Col xs={12} md={6}>
          <Button
            fullwidth
            loading={isSaving}
            text={t(`${dictPrefix}.processesForm.save`)}
            onClick={onSave}
          />
        </Col>
      </Row>
    </Modal>
  );
}
